import React from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import { Box, Text, Alert, Spinner, useAsyncCallback } from '@wix/cli-ui-kit';
import { useARMClient } from '@wix/arm-client';
import { useBackendAsAServiceClient } from '@wix/backend-as-a-service-client';
import { useSiteConfig } from '../../model.js';
import { useUpdateDeploymentTopologyForSite } from '../../hooks/useUpdateDeploymentTopology.js';
import { ActionStatus } from '../PreviewCommand/ActionStatus.js';
import { useCreatePreview } from '../PreviewCommand/hooks/useCreatePreview.js';

interface ReleaseProps {
  baseUrl?: string;
  label?: string;
  comment?: string;
  createMinorVersion: boolean;
}

export const ReleaseSite: FC<ReleaseProps> = ({
  baseUrl,
  label,
  createMinorVersion,
  comment,
}) => {
  const { appId, siteId } = useSiteConfig();

  const { getAppProject } = useBackendAsAServiceClient();
  const { releaseHeadlessProject } = useARMClient();
  const updateDeploymentTopology = useUpdateDeploymentTopologyForSite(siteId);

  const releaseAction = useAsyncCallback(
    async (_, previewId: string, previewUrl?: string) => {
      const deploymentBaseUrl = await releaseHeadlessProject({
        appId,
        componentOverrideId: previewId,
        createMinorVersion,
        comment,
      });

      await updateDeploymentTopology({
        environment: 'production',
        siteUrl: previewUrl ?? '',
        versionOverrideId: previewId,
      });

      // TODO: Remove once BaaS return the Custom Premium Domain from the Release API
      const appProject = await getAppProject(appId);

      return { baseUrl: appProject?.customDomain?.domain ?? deploymentBaseUrl };
    }
  );

  const { uploadStatus } = useCreatePreview({
    baseUrl,
    label,
    onPreviewCreated: ({ previewId, previewUrl }) => {
      void releaseAction.execute(previewId, previewUrl);
    },
  });

  return (
    <>
      <ActionStatus
        status={uploadStatus}
        loadingText={'Uploading your application files...'}
        successText={'Uploaded successfully!'}
      />
      {match(releaseAction.status, {
        NotRequested: () => null,
        Error: () => null,
        Loading: () => <Spinner text={<Text>Releasing...</Text>} />,
        Success: ({ result: { baseUrl } }) => (
          <Box flexDirection="column" gap={1}>
            <Alert type="success">
              <Text bold>Site published on {baseUrl}</Text>
            </Alert>
          </Box>
        ),
      })}
    </>
  );
};
